import { Controller, useFormContext } from "react-hook-form";
import { TextField } from "~/common/m3/TextField/TextField";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useTranslation } from "react-i18next";
import type { MediaFilterData, MediaFormFields } from "~/MediaLibrary/types/mediaLibraryFormTypes";
import { memo, useCallback, useEffect, useMemo } from "react";
import classes from "./MediaLibrarySearchBox.module.less";
import type { Theme } from "@mui/material";
import type { SxProps } from "@mui/material";
import { debounce } from "lodash";
import SortByFilter from "../SortByFilter/SortByFilter";
import MediaLibraryFilters from "./MediaLibraryFilters";
import { trackMediaLibraryFilterActions } from "~/feature/Analytics/ga/mediaLibrary";

export const searchInputStyles: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
};

const inputSlotProps = {
  input: {
    startAdornment: (
      <div className={classes.searchIcon}>
        <Icon type={IconType.Search} />
      </div>
    ),
  },
};

const MediaLibrarySearchBox = memo(function MediaLibrarySearchBox(props: {
  filterOptions: MediaFilterData;
  submitForm: (data: MediaFormFields) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { submitForm, filterOptions } = props;

  const { control, watch, handleSubmit, getValues } = useFormContext<MediaFormFields>();

  const onSubmit = useCallback(
    (data: MediaFormFields) => {
      submitForm(data);
    },
    [submitForm],
  );

  const debouncedSubmit = useMemo(() => debounce(() => onSubmit(getValues()), 500), [onSubmit]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      const key = name as keyof MediaFormFields;
      if (name === "q") {
        debouncedSubmit();
      } else {
        trackMediaLibraryFilterActions(value?.q, key, value[key] as string);
        onSubmit(getValues());
      }
    });

    return () => {
      subscription.unsubscribe();
      debouncedSubmit.cancel();
    };
  }, [watch, debouncedSubmit]);

  return (
    <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.searchBar}>
        <Controller
          name="q"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TextField
              placeholder={t("generic.button.search")}
              className={classes.searchInput}
              sx={searchInputStyles}
              slotProps={inputSlotProps}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <SortByFilter />
      </div>
      <MediaLibraryFilters filterOptions={filterOptions} />
    </form>
  );
});

export default MediaLibrarySearchBox;
